import { SearchOutlined } from '@ant-design/icons';
import React, { useRef, useState, useEffect } from 'react';
import Highlighter from 'react-highlight-words';
import { Button, Input, Space, Table } from 'antd';
import { Link, useNavigate} from "react-router-dom";
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import firebase from '../../config/firebase';
import { useSelector } from 'react-redux';
import InputMask from 'react-input-mask';
import VisibilityIcon from '@mui/icons-material/Visibility';
import shipperService from '../../service/shipper.service';
import ModalFilter from '../ModalFilter/ModalFilter';

require('firebase/auth')

const DataEmbarcadorTable = ({ cleanFilters, openFilters, onCleanFilterHandled, onOpenFilterHandled }) => {

  let navigate = useNavigate();
  const user = useSelector(state => state.user)

  const [searchText, setSearchText] = useState('');
  const [searchedColumn, setSearchedColumn] = useState('');
  const [datas, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [columnFilters, setColumnFilters] = useState({});
  const [sorterConfig, setSorterConfig] = useState({ columnKey: null, order: null });
  const [openModalFilter, setOpenModalFilter] = useState(false);
  const hasLoadedRef = useRef(false);
  const searchInput = useRef(null);
  const storageKey = (uid, key) => `${uid}:${key}`;
  const shipperModalFilterColumns = [
    { key: "socialName", label: "Razão Social", type: "text" },
    { key: "nameFantasy", label: "Nome Fantasia", type: "text" },
    { key: "documentNumber", label: "CNPJ", type: "text" },
  ];
  
  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
    setColumnFilters((prev) => ({
      ...prev,
      [dataIndex]: selectedKeys?.length ? selectedKeys : null,
    }));
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      if(user.viewALL) {
        const snapshot = await firebase.firestore().collection('shipper').get();
        const documents = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setData(documents);
        setLoading(false);
      }
      else if (user?.uidShipperAffiliates && Array.isArray(user.uidShipperAffiliates)) {
        const affiliateUids = Array.isArray(user?.uidShipperAffiliates) ? user.uidShipperAffiliates : [];
        const primaryUid = user?.uidShipper ? [user.uidShipper] : [];
        const uids = Array.from(new Set([...affiliateUids, ...primaryUid]));

        if (uids.length > 1) {
          const snapshot = await firebase.firestore().collection('shipper')
            .where('uidShipper', 'in', uids)
            .get();
          const documents = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
          setData(documents);
          setLoading(false);
        }
        else {
          setLoading(false);
          navigate("/newEmbarcador/" + user.uidShipper);
        }
      }
      else {
        setLoading(false);
        navigate("/newEmbarcador/" + user.uidShipper);
      }
    } catch (error) {
      console.error("Erro ao buscar dados dos embarcadores:", error);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (!user?.uid) return;

    try {
      const savedColumnFilters = localStorage.getItem(
        storageKey(user.uid, "shipperColumnFilters"),
      );
      const savedSorter = localStorage.getItem(
        storageKey(user.uid, "shipperTableSorter"),
      );

      setColumnFilters(savedColumnFilters ? JSON.parse(savedColumnFilters) : {});
      setSorterConfig(
        savedSorter
          ? JSON.parse(savedSorter)
          : { columnKey: null, order: null },
      );
    } catch (error) {
      console.error("Erro ao carregar filtros do localStorage:", error);
      setColumnFilters({});
      setSorterConfig({ columnKey: null, order: null });
    } finally {
      hasLoadedRef.current = true;
    }
  }, [user?.uid]);

  useEffect(() => {
    if (!hasLoadedRef.current || !user?.uid) return;

    const hasActiveColumnFilters = Object.values(columnFilters || {}).some(
      (values) => Array.isArray(values) && values.length > 0
    );

    if (hasActiveColumnFilters) {
      localStorage.setItem(
        storageKey(user.uid, "shipperColumnFilters"),
        JSON.stringify(columnFilters),
      );
    } else {
      localStorage.removeItem(storageKey(user.uid, "shipperColumnFilters"));
    }

    if (sorterConfig?.columnKey && sorterConfig?.order) {
      localStorage.setItem(
        storageKey(user.uid, "shipperTableSorter"),
        JSON.stringify(sorterConfig),
      );
    } else {
      localStorage.removeItem(storageKey(user.uid, "shipperTableSorter"));
    }
  }, [user?.uid, columnFilters, sorterConfig]);

  useEffect(() => {
    if (cleanFilters) {
      setSearchText('');
      setSearchedColumn('');
      setColumnFilters({});
      setSorterConfig({ columnKey: null, order: null });

      if (user?.uid) {
        localStorage.removeItem(storageKey(user.uid, "shipperColumnFilters"));
        localStorage.removeItem(storageKey(user.uid, "shipperTableSorter"));
      }

      onCleanFilterHandled?.();
    }
  }, [cleanFilters, user?.uid, onCleanFilterHandled]);

  useEffect(() => {
    if (openFilters) {
      setOpenModalFilter(true);
      onOpenFilterHandled?.();
    }
  }, [openFilters, onOpenFilterHandled]);

  const handleApplyCustomFilter = (filter) => {
    const newFilters = {};

    if (filter.conditions && filter.conditions.length > 0) {
      filter.conditions.forEach(condition => {
        const { column, operator, value } = condition;

        if (!column) return;

        if (
          (operator === 'contains' ||
            operator === 'equals' ||
            operator === 'startsWith' ||
            operator === 'endsWith') &&
          value !== undefined &&
          value !== null &&
          value !== ''
        ) {
          newFilters[column] = [String(value)];
        }
      });
    }

    setColumnFilters(newFilters);
  };

  const handleReset = (clearFilters, dataIndex) => {
    clearFilters();
    setSearchText('');
    setColumnFilters((prev) => {
      const nextFilters = { ...prev };
      delete nextFilters[dataIndex];
      return nextFilters;
    });
  };

  const deleteEmb = async (id) => {
    const result = window.confirm("Deseja mesmo excluir esse embarcador?");
    if (!result) return;

    try {
      await shipperService.deleteShipper(id);
      alert("Embarcador excluído com sucesso!");
      fetchData();
    } catch (error) {
      console.error("Erro ao excluir embarcador:", error);
      alert("Erro ao excluir o embarcador.");
    }
  };

  const getColumnSearchProps = (dataIndex) => ({
    filteredValue: columnFilters[dataIndex] || null,
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
      <div
        style={{
          padding: 8,
        }}
        onKeyDown={(e) => e.stopPropagation()}
      >
        <Input
          ref={searchInput}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{
            marginBottom: 8,
            display: 'block',
          }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
            icon={<SearchOutlined />}
            size="small"
            style={{
              width: 90,
            }}
          >
            Search
          </Button>
          <Button
            onClick={() => clearFilters && handleReset(clearFilters, dataIndex)}
            size="small"
            style={{
              width: 90,
            }}
          >
            Reset
          </Button>
          <Button
            type="link"
            size="small"
            onClick={() => {
              confirm({
                closeDropdown: false,
              });
              setSearchText(selectedKeys[0]);
              setSearchedColumn(dataIndex);
            }}
          >
            Filter
          </Button>
          <Button
            type="link"
            size="small"
            onClick={() => {
              close();
            }}
          >
            close
          </Button>
        </Space>
      </div>
    ),
    
    filterIcon: (filtered) => (
      <SearchOutlined
        style={{
          color: filtered ? '#1677ff' : undefined,
        }}
      />
    ),

    onFilter: (value, record) =>{ 

      if (dataIndex === 'documentNumber') {
        const cleanValue = value.replace(/[^\w]/g, '').trim();

        const documentNumber = record.dataPersonal.documentNumber
          ? record.dataPersonal.documentNumber.toString().replace(/[^\w]/g, '').toLowerCase().trim()
          : '';

        return documentNumber.includes(cleanValue.toLowerCase());
      }
      else if (dataIndex === 'socialName') {
        return (
          (record.dataPersonal.socialName && record.dataPersonal.socialName.toString().toLowerCase().includes(value.toLowerCase()))
        );
      }
      else if(dataIndex === 'nameFantasy'){
        return (
          (record.dataPersonal.nameFantasy && record.dataPersonal.nameFantasy.toString().toLowerCase().includes(value.toLowerCase()))
        );
      }
      else {
        return record?.[dataIndex]?.toString().toLowerCase().includes(value.toLowerCase());
      }

    }, onFilterDropdownOpenChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100);
      }
    },

    render: (text) =>
      searchedColumn === dataIndex ? (
        <Highlighter
          highlightStyle={{
            backgroundColor: '#ffc069',
            padding: 0,
          }}
          searchWords={[searchText]}
          autoEscape
          textToHighlight={text ? text.toString() : ''}
        />
      ) : (
        text
      ),
  });

  const columns = [
    {
      title: 'Razão Social',
      dataIndex: 'socialName',
      key: 'socialName',
      width: '30%',
      ...getColumnSearchProps('socialName'),
      sortOrder: sorterConfig.columnKey === 'socialName' ? sorterConfig.order : null,
      render: (_, record) => (
        <span>
          {record.dataPersonal.socialName}
        </span>
      ),
      sorter: (a, b) => {
        const nameA = a.dataPersonal?.socialName || '';
        const nameB = b.dataPersonal?.socialName || '';
        return nameA.localeCompare(nameB);
      },
    },
    {
      title: 'Nome Fantasia',
      dataIndex: 'nameFantasy',
      key: 'nameFantasy',
      width: '30%',
      ...getColumnSearchProps('nameFantasy'),
      sortOrder: sorterConfig.columnKey === 'nameFantasy' ? sorterConfig.order : null,
      render: (_, record) => (
        <span>
          {record.dataPersonal.nameFantasy}
        </span>
      ),
      sorter: (a, b) => {
        const nameA = a.dataPersonal?.nameFantasy || '';
        const nameB = b.dataPersonal?.nameFantasy || '';
        return nameA.localeCompare(nameB);
      },
    },
    {
      title: 'CNPJ',
      dataIndex: 'documentNumber',
      key: 'documentNumber',
      ...getColumnSearchProps('documentNumber'),
      sortOrder: sorterConfig.columnKey === 'documentNumber' ? sorterConfig.order : null,
      render: (_, record) => (
        <span>
          <InputMask mask="99.999.999/9999-99" value={record.dataPersonal.documentNumber} disabled style={{
          border: 'none',
          backgroundColor: 'transparent',
          width: 'auto',
          padding: '0 5px',
          fontSize: '14px',
          color: '#444',
        }}/>
        </span>
      ),
      sorter: (a, b) => {
        const nameA = a.dataPersonal?.documentNumber || '';
        const nameB = b.dataPersonal?.documentNumber || '';
        return nameA.localeCompare(nameB);
      },
    },
    {
      title: 'Ações',
      width: '10%',
      // dataIndex: 'data',
      key: 'action',
          render: (_, record) => (
              <Space size="middle">
                {
                  user?.permissoes?.shipper?.isEdit || user?.permissoes?.embarcador?.editar ?
                  (
                    <Link to={'/newEmbarcador/' + record.id}>
                      <EditOutlinedIcon />
                    </Link>
                  )
                  :
                  (
                    <Link to={'/newEmbarcador/' + record.id} state={{viewOnly: true}}>
                      <VisibilityIcon />
                    </Link>
                  )
                }
                {
                  user?.permissoes?.shipper?.isDelete || user?.permissoes?.embarcador?.excluir ? 
                  (
                    <Link to={'#'}>
                      <DeleteOutlinedIcon onClick={(e) => deleteEmb(record.id)} color='error'/>
                    </Link>
                  )
                  :
                  (
                    ""
                  )
                }
              </Space>
          ),
    },
  ];

  return (
    <>
      <ModalFilter
        visible={openModalFilter}
        onClose={() => setOpenModalFilter(false)}
        onApply={handleApplyCustomFilter}
        availableColumns={shipperModalFilterColumns}
        storageKeySuffix="shipperCustomFilters"
      />
      <Table
        columns={columns}
        dataSource={datas}
        loading={loading}
        onChange={(_, filters, sorter) => {
          setColumnFilters(filters || {});

          if (!sorter || Array.isArray(sorter)) {
            setSorterConfig({ columnKey: null, order: null });
            return;
          }

          setSorterConfig({
            columnKey: sorter.columnKey || sorter.field || null,
            order: sorter.order || null,
          });
        }}
      />
    </>
  );

};
export default DataEmbarcadorTable;